import { useEffect, useState, type FormEvent } from 'react';
import { Plus, RefreshCw, ShieldCheck, AlertTriangle, X, FlaskConical } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import {
  useEprouvettes, useCreateEprouvette, useTestEprouvette, useEcarterEprouvette,
  useDeleteEprouvette, useDashboardQC, useGachees,
} from '@/hooks/useBetonSprint2';
import { STATUT_EPROUVETTE_LABELS, type StatutEprouvette, type Eprouvette } from '@/types';
import { extractError } from '@/api/client';
import { demander } from '@/components/ui/Fenetres';

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });

const STATUT_COLOR: Record<StatutEprouvette, string> = {
  moulee:         'var(--tx-3)',
  en_attente_r7:  'var(--info)',
  en_attente_r28: 'var(--warning)',
  conforme:       'var(--success)',
  non_conforme:   'var(--danger)',
  ecartee:        'var(--tx-3)',
};

export const ControleQualitePage = () => {
  const [statut, setStatut]         = useState<StatutEprouvette | ''>('');
  const [echeance, setEcheance]     = useState<number | ''>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [testFor, setTestFor]       = useState<Eprouvette | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isLoading, refetch } = useEprouvettes({
    statut:        statut || undefined,
    joursEcheance: echeance === '' ? undefined : echeance,
    limit: 200,
  });
  const { data: dash } = useDashboardQC();
  const ecarter = useEcarterEprouvette();
  const remove  = useDeleteEprouvette();
  const eprouvettes = data?.data ?? [];

  const handle = async (fn: () => Promise<any>, msg?: string) => {
    if (msg && !(await demander.confirmer({ titre: msg, danger: /supprim|annul|rejet|refus|désactiv|écart/i.test(msg) }))) return;
    setActionError(null);
    try { await fn(); } catch (e) { setActionError(extractError(e).message); }
  };

  return (
    <>
      <Topbar
        title="Contrôle qualité béton"
        subtitle="Module 5 — Éprouvettes R7 / R28 · taux conformité ≥97%"
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}>
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            </button>
            <button className="ds-btn ds-btn-p" onClick={() => setDialogOpen(true)}>
              <Plus size={14} /> Nouvelle éprouvette
            </button>
          </>
        }
      />
      <div className="ds-content">
        {actionError && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">{actionError}</div>
          </div>
        )}

        {/* Dashboard QC */}
        {dash && (
          <div className="ds-kpi-strip">
            <div className={`ds-ks ${dash.periode90j.tauxConformite != null && dash.periode90j.tauxConformite >= 97 ? 'ks-green' : 'ks-orange'}`}>
              <div className="ds-ks-label">Taux conformité 90j</div>
              <div className="ds-ks-value">{dash.periode90j.tauxConformite != null ? `${dash.periode90j.tauxConformite}%` : '—'}</div>
              <div className="ds-ks-sub">Objectif ≥97%</div>
            </div>
            <div className="ds-ks ks-blue">
              <div className="ds-ks-label">Éprouvettes 90j</div>
              <div className="ds-ks-value">{dash.periode90j.total}</div>
              <div className="ds-ks-sub">
                {dash.periode90j.conformes} ✓ · {dash.periode90j.nonConformes} ✗
                {dash.periode90j.ecartees > 0 && ` · ${dash.periode90j.ecartees} écartées`}
              </div>
            </div>
            <div className={`ds-ks ${dash.attenteR28 > 0 ? 'ks-orange' : 'ks-green'}`}>
              <div className="ds-ks-label">Attente R28</div>
              <div className="ds-ks-value">{dash.attenteR28}</div>
              <div className="ds-ks-sub">À écraser</div>
            </div>
            <div className="ds-ks ks-purple">
              <div className="ds-ks-label">Courbe R28</div>
              <div className="ds-ks-value">{dash.courbeR28.length}</div>
              <div className="ds-ks-sub">Points 90j</div>
            </div>
          </div>
        )}

        <div className="ds-filters">
          <select className="ds-fi" value={statut} onChange={(e) => setStatut(e.target.value as StatutEprouvette | '')} style={{ width: 'auto' }}>
            <option value="">Tous statuts</option>
            {(Object.keys(STATUT_EPROUVETTE_LABELS) as StatutEprouvette[]).map((s) => (
              <option key={s} value={s}>{STATUT_EPROUVETTE_LABELS[s]}</option>
            ))}
          </select>
          <select className="ds-fi" value={echeance} onChange={(e) => setEcheance(e.target.value === '' ? '' : parseInt(e.target.value, 10))} style={{ width: 'auto' }}>
            <option value="">Toutes échéances</option>
            <option value="0">Aujourd'hui</option>
            <option value="3">≤ 3 jours</option>
            <option value="7">≤ 7 jours</option>
            <option value="14">≤ 14 jours</option>
          </select>
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">Éprouvettes</div>
              <div className="ds-table-sub">{eprouvettes.length} éprouvette{eprouvettes.length > 1 ? 's' : ''}</div>
            </div>
          </div>
          <table className="ds-tbl">
            <thead>
              <tr>
                <th style={{ width: 130 }}>Référence</th>
                <th style={{ width: 130 }}>Gâchée · OF</th>
                <th style={{ width: 100 }}>Moulage</th>
                <th style={{ width: 100 }}>R7 prévue</th>
                <th style={{ width: 80 }}>fc7</th>
                <th style={{ width: 100 }}>R28 prévue</th>
                <th style={{ width: 100 }}>fc28 / cible</th>
                <th style={{ width: 90 }}>Statut</th>
                <th style={{ width: 160 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 32, color: 'var(--tx-3)' }}>Chargement…</td></tr>
              )}
              {!isLoading && eprouvettes.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: 48 }}>
                    <FlaskConical size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                    <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucune éprouvette</p>
                  </td>
                </tr>
              )}
              {!isLoading && eprouvettes.map((e) => {
                const fc28      = e.fc28Mesure ? parseFloat(e.fc28Mesure) : null;
                const fc28Cible = e.fc28CibleMpa ? parseFloat(e.fc28CibleMpa) : null;
                return (
                  <tr key={e.id}>
                    <td><span className="ds-ref-code">{e.reference}</span></td>
                    <td>
                      <div className="ds-entry-label">{e.gachee?.reference ?? '—'}</div>
                      <div className="ds-entry-sub">{e.gachee?.ordre?.reference ?? '—'}</div>
                    </td>
                    <td style={{ fontSize: 11, color: 'var(--tx-2)' }}>{fmtDate(e.dateMoulage)}</td>
                    <td style={{ fontSize: 11 }}>
                      {e.dateR7Reelle ? <span style={{ color: 'var(--success)' }}>✓ {fmtDate(e.dateR7Reelle)}</span> : <span style={{ color: 'var(--tx-3)' }}>{fmtDate(e.dateR7Prevue)}</span>}
                    </td>
                    <td style={{ fontSize: 11 }}>{e.fc7Mesure ?? '—'}</td>
                    <td style={{ fontSize: 11 }}>
                      {e.dateR28Reelle ? <span style={{ color: 'var(--success)' }}>✓ {fmtDate(e.dateR28Reelle)}</span> : <span style={{ color: 'var(--tx-3)' }}>{fmtDate(e.dateR28Prevue)}</span>}
                    </td>
                    <td style={{ fontSize: 11 }}>
                      {fc28 != null ? (
                        <span style={{ color: fc28Cible != null && fc28 < fc28Cible ? 'var(--danger)' : 'var(--success)', fontWeight: 600 }}>
                          {fc28}
                        </span>
                      ) : <span style={{ color: 'var(--tx-3)' }}>—</span>}
                      {fc28Cible != null && <span style={{ color: 'var(--tx-3)' }}> / {fc28Cible}</span>}
                    </td>
                    <td>
                      <span className="ds-bd" style={{ background: STATUT_COLOR[e.statut], color: 'white', fontSize: 10 }}>
                        {STATUT_EPROUVETTE_LABELS[e.statut]}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {(e.statut === 'moulee' || e.statut === 'en_attente_r7' || e.statut === 'en_attente_r28') && (
                          <button className="ds-btn ds-btn-p ds-btn-sm" onClick={() => setTestFor(e)} title="Saisir essai">
                            <FlaskConical size={11} />
                          </button>
                        )}
                        {(e.statut === 'moulee' || e.statut === 'en_attente_r7' || e.statut === 'en_attente_r28') && (
                          <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => handle(async () => { const motif = await demander.texte({ titre: 'Motif écartement ?', confirmer: 'Écarter' }); return ecarter.mutateAsync({ id: e.id, motif: motif ?? undefined }); }, 'Écarter cette éprouvette ?')} title="Écarter" style={{ color: 'var(--warning)' }}>
                            <AlertTriangle size={11} />
                          </button>
                        )}
                        <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => handle(() => remove.mutateAsync(e.id), `Supprimer ${e.reference} ?`)} title="Supprimer">
                          <X size={11} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <EprouvetteFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
      <TestEprouvetteDialog eprouvette={testFor} onClose={() => setTestFor(null)} />
    </>
  );
};

// ── Dialog création éprouvette ──────────────────────────

const EprouvetteFormDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const [gacheeId, setGacheeId]       = useState('');
  const [typeEp, setTypeEp]           = useState('cylindre_16x32');
  const [dateMoulage, setDateMoulage] = useState(new Date().toISOString().slice(0, 10));
  const [fc28Cible, setFc28Cible]     = useState('');
  const [notes, setNotes]             = useState('');
  const [error, setError]             = useState<string | null>(null);

  const { data: gacheesData } = useGachees({ statut: 'valide', limit: 100 });
  const create = useCreateEprouvette();
  const gachees = gacheesData?.data ?? [];

  useEffect(() => {
    if (open) {
      setGacheeId(''); setTypeEp('cylindre_16x32'); setFc28Cible(''); setNotes(''); setError(null);
      setDateMoulage(new Date().toISOString().slice(0, 10));
    }
  }, [open]);

  const selectedGachee = gachees.find((g) => g.id === gacheeId);
  const fc28FromFormule = selectedGachee?.formuleBeton?.fc28TargetMpa;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({
        gacheeId,
        typeEprouvette: typeEp,
        dateMoulage,
        fc28CibleMpa: fc28Cible ? parseFloat(fc28Cible) : undefined,
        notes: notes.trim() || undefined,
      });
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Nouvelle éprouvette" subtitle="Réf auto EP-YYYY-NNNNN · R7 & R28 calculées auto" size="md">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}

        <Field label="Gâchée validée *">
          {gachees.length === 0 ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
              Aucune gâchée validée. Validez une gâchée d'abord.
            </div>
          ) : (
            <select value={gacheeId} onChange={(e) => setGacheeId(e.target.value)}
              className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm" required>
              <option value="">— Sélectionner —</option>
              {gachees.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.reference} · OF {g.ordre?.reference ?? '?'}
                  {g.formuleBeton ? ` · ${g.formuleBeton.code}` : ''}
                </option>
              ))}
            </select>
          )}
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Type d'éprouvette">
            <select value={typeEp} onChange={(e) => setTypeEp(e.target.value)}
              className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm">
              <option value="cylindre_16x32">Cylindre 16×32</option>
              <option value="cube_15">Cube 15</option>
              <option value="autre">Autre</option>
            </select>
          </Field>
          <Field label="Date moulage">
            <input type="date" value={dateMoulage} onChange={(e) => setDateMoulage(e.target.value)}
              className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm" />
          </Field>
        </div>

        <Field label={`fc28 cible (MPa) ${fc28FromFormule ? `— formule = ${fc28FromFormule}` : ''}`}>
          <input type="number" min="0" step="0.5" value={fc28Cible} onChange={(e) => setFc28Cible(e.target.value)}
            className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
            placeholder={fc28FromFormule ? `Auto = ${fc28FromFormule}` : '30'} />
        </Field>

        <Field label="Notes">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
            className="flex w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm" placeholder="Observations…" />
        </Field>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!gacheeId || create.isPending}>
            <Plus size={14} /> {create.isPending ? 'Création…' : 'Créer éprouvette'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

// ── Dialog test R7/R28 ──────────────────────────────────

const TestEprouvetteDialog = ({ eprouvette, onClose }: { eprouvette: Eprouvette | null; onClose: () => void }) => {
  const open = eprouvette !== null;
  const test = useTestEprouvette();
  const [fc, setFc]         = useState('');
  const [dateTest, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes]   = useState('');
  const [error, setError]   = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setFc(''); setDate(new Date().toISOString().slice(0, 10)); setNotes(''); setError(null);
    }
  }, [open]);

  if (!eprouvette) return null;

  const testType = eprouvette.statut === 'en_attente_r7' || eprouvette.statut === 'moulee' ? 'R7' : 'R28';
  const fc28Cible = eprouvette.fc28CibleMpa ? parseFloat(eprouvette.fc28CibleMpa) : null;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await test.mutateAsync({ id: eprouvette.id, payload: { fcMesure: parseFloat(fc), dateTest, notes: notes || undefined } });
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  const fcNum = fc ? parseFloat(fc) : null;
  const nonConforme = testType === 'R28' && fcNum != null && fc28Cible != null && fcNum < fc28Cible;

  return (
    <Dialog open={open} onClose={onClose} title={`Essai ${testType} — ${eprouvette.reference}`} subtitle={fc28Cible ? `fc28 cible = ${fc28Cible} MPa` : 'Pas de cible définie'} size="md">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}

        <Field label={`Résistance ${testType} mesurée (MPa) *`}>
          <input type="number" min="0" step="0.5" value={fc} onChange={(e) => setFc(e.target.value)}
            className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm" required placeholder="32" />
        </Field>

        <Field label="Date du test">
          <input type="date" value={dateTest} onChange={(e) => setDate(e.target.value)}
            className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm" />
        </Field>

        <Field label="Notes">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
            className="flex w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm" />
        </Field>

        {nonConforme && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800">
            ⚠ fc28 mesurée {fc} &lt; cible {fc28Cible} MPa → statut "non_conforme". Pensez à créer une fiche DNC.
          </div>
        )}
        {!nonConforme && fcNum != null && testType === 'R28' && fc28Cible != null && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
            ✓ fc28 mesurée {fc} ≥ cible {fc28Cible} MPa → conforme NF EN 206
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!fc || test.isPending}>
            <ShieldCheck size={14} /> {test.isPending ? 'Enregistrement…' : `Valider essai ${testType}`}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
    {children}
  </div>
);
